import Container from "./Container";
import { Link } from "react-router-dom";
import { useStorefrontConfig } from "@/features/checkout/api/useStorefrontConfig";
import TrustBadges from "@/features/trust/TrustBadges";
import { buildStorePath } from "@/lib/store-path";
import { findPolicy, hasContact } from "@/lib/storefront-trust";

// Slice 11 — vitrina honesta: nombre, sellos, políticas y contacto salen del
// bootstrap del negocio. Sin textos fijos de marketing, sin newsletter (no
// existe) y sin redes sociales vacías.
export default function Footer() {
  const { data: config } = useStorefrontConfig();
  const trust = config?.trust;
  const storeName = config?.storeName ?? "";
  const year = new Date().getFullYear();

  const policyLinks = trust?.policyLinks ?? [];
  const terms = trust ? findPolicy(trust, "terms") : null;
  const showContact = !!trust && hasContact(trust);

  function scrollTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const linkCls =
    "text-sm opacity-80 hover:opacity-100 transition hover:underline underline-offset-4 decoration-[var(--ring)]";

  return (
    <footer className="mt-10 border-t border-[rgb(var(--border-rgb))] bg-[color-mix(in_oklab,var(--card) 92%,transparent)]">
      {/* Footer full-width tipo Amazon */}
      <Container className="!max-w-none px-4 sm:px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-3">
            {storeName ? (
              <Link to={buildStorePath("")} className="inline-block">
                <span className="text-xl font-extrabold bg-gradient-to-r from-[var(--brand-start)] to-[var(--brand-end)] bg-clip-text text-transparent">
                  {storeName}
                </span>
              </Link>
            ) : null}
            {trust?.footerTagline ? (
              <p className="text-sm opacity-80 leading-relaxed">
                {trust.footerTagline}
              </p>
            ) : null}

            <TrustBadges trust={trust} />
          </div>

          {/* Tienda */}
          <nav aria-label="Tienda" className="space-y-3">
            <h3 className="text-sm font-semibold">Tienda</h3>
            <ul className="space-y-2">
              <li>
                <Link className={linkCls} to={buildStorePath("/tienda?sort=newest")}>
                  Novedades
                </Link>
              </li>
              <li>
                <Link className={linkCls} to={buildStorePath("/tienda?sort=price_asc")}>
                  Precios ↑
                </Link>
              </li>
              <li>
                <Link className={linkCls} to={buildStorePath("/tienda?sort=price_desc")}>
                  Precios ↓
                </Link>
              </li>
              <li>
                <Link className={linkCls} to={buildStorePath("/tienda")}>
                  Ver todo
                </Link>
              </li>
              <li>
                <Link className={linkCls} to={buildStorePath("/cart")}>
                  Carrito
                </Link>
              </li>
            </ul>
          </nav>

          {/* Cuenta */}
          <nav aria-label="Tu cuenta" className="space-y-3">
            <h3 className="text-sm font-semibold">Tu cuenta</h3>
            <ul className="space-y-2">
              <li>
                <Link className={linkCls} to={buildStorePath("/account/orders")}>
                  Mis pedidos
                </Link>
              </li>
              <li>
                <Link className={linkCls} to={buildStorePath("/account/addresses")}>
                  Direcciones
                </Link>
              </li>
              <li>
                <button onClick={scrollTop} className={linkCls}>
                  Volver arriba
                </button>
              </li>
            </ul>
          </nav>

          {/* Información: solo páginas que el negocio editó + contacto real */}
          {policyLinks.length > 0 || showContact ? (
            <nav aria-label="Información" className="space-y-3">
              <h3 className="text-sm font-semibold">Información</h3>
              <ul className="space-y-2">
                {policyLinks.map((p) => (
                  <li key={p.slug}>
                    <Link className={linkCls} to={buildStorePath(`/pages/${p.slug}`)}>
                      {p.title}
                    </Link>
                  </li>
                ))}
                {showContact ? (
                  <li>
                    <Link className={linkCls} to={buildStorePath("/contact")}>
                      Contacto
                    </Link>
                  </li>
                ) : null}
              </ul>
            </nav>
          ) : null}
        </div>

        {/* Barra inferior */}
        <div className="mt-8 pt-6 border-t border-[rgb(var(--border-rgb))] flex flex-col md:flex-row items-center justify-between gap-3 text-sm opacity-80">
          <div>
            © {year}
            {storeName ? ` ${storeName}` : ""}. Todos los derechos reservados.
          </div>
          {terms || showContact ? (
            <div className="flex items-center gap-4">
              {terms ? (
                <Link to={buildStorePath("/pages/terms")} className={linkCls}>
                  {terms.title}
                </Link>
              ) : null}
              {showContact ? (
                <Link to={buildStorePath("/contact")} className={linkCls}>
                  Contacto
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </Container>
    </footer>
  );
}
